import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { canViewAI, STATE_STYLE, fmtDateTime } from "../lib/states";
import LeadDrawer from "../components/LeadDrawer";
import { Sparkles, RefreshCcw, Loader2, AlertTriangle, Check, TrendingUp, Clock, Lightbulb } from "lucide-react";

const SEV_DOT = { high: "bg-red-500", medium: "bg-amber-500", low: "bg-stone-400" };
const CAT_META = {
  missed_action: { label: "Missed", icon: Clock,       accent: "border-red-200 bg-red-50/30" },
  opportunity:   { label: "Signal", icon: TrendingUp,  accent: "border-emerald-200 bg-emerald-50/30" },
};

function InsightCard({ it, onOpen, onDismiss }) {
  const m = CAT_META[it.category] || CAT_META.missed_action;
  const Icon = m.icon;
  const [busy, setBusy] = useState(false);
  const handleDismiss = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try { await onDismiss(it.id); } finally { setBusy(false); }
  };
  return (
    <div data-testid={`ai-item-${it.id}`}
      onClick={() => onOpen(it.lead_id)}
      className={`group border rounded-lg p-3.5 transition-all cursor-pointer hover:shadow-sm ${m.accent}`}>
      <div className="flex items-start gap-2.5">
        <Icon className="h-4 w-4 mt-0.5 shrink-0 text-stone-600" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`h-1.5 w-1.5 rounded-full ${SEV_DOT[it.severity] || SEV_DOT.medium}`} />
            <span className="text-[10px] uppercase tracking-wider font-semibold text-stone-500">{m.label}</span>
            <span className="text-sm font-semibold text-stone-900 truncate">{it.title || it.lead_name}</span>
          </div>
          <p className="text-xs text-stone-700 mt-1 leading-snug">{it.summary}</p>
          {it.recommendation && (
            <p className="text-xs text-stone-600 mt-1 leading-snug">
              <span className="text-stone-400">→ </span>{it.recommendation}
            </p>
          )}
          <div className="flex items-center justify-between gap-2 mt-2">
            <div className="flex items-center gap-1.5 text-[11px] text-stone-500 min-w-0">
              <span className="truncate font-medium text-stone-700">{it.lead_name}</span>
              {it.state && <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${STATE_STYLE[it.state]?.badge || ""}`}>{it.state}</span>}
            </div>
            <button onClick={handleDismiss} disabled={busy}
              data-testid={`mark-read-${it.id}`}
              className="text-[11px] inline-flex items-center gap-1 text-stone-500 hover:text-stone-900 hover:bg-white border border-transparent hover:border-stone-200 rounded-md px-2 py-1 transition-colors disabled:opacity-60">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
              Mark as read
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AIInsights() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeId, setActiveId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/ai-insights")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data: d } = await api.post("/ai-insights/refresh");
      setData(d);
      if (d.gemini_error) {
        toast.warning(`Gemini unavailable: ${d.gemini_error}`);
      } else {
        toast.success(d.analysed_this_run === 0
          ? "No new comments since the last run."
          : `Analysed ${d.analysed_this_run} updated lead${d.analysed_this_run === 1 ? "" : "s"}.`);
      }
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setRefreshing(false); }
  };

  const dismiss = async (id) => {
    try {
      await api.post(`/ai-insights/${id}/dismiss`);
      setData((d) => d && ({ ...d, items: (d.items || []).filter((it) => it.id !== id) }));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  if (!canViewAI(user)) {
    return <div className="p-6 text-sm text-stone-500">Not available for your role.</div>;
  }

  const items = data?.items || [];
  const missed = items.filter((i) => i.category === "missed_action");
  const opps   = items.filter((i) => i.category === "opportunity");
  const patterns = data?.patterns || [];
  const recs = data?.recommendations || [];
  const empty = !data?.last_run_at;

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
      <header className="px-5 sm:px-6 pt-5 pb-3 border-b border-stone-200 bg-white shrink-0">
        <div className="flex flex-wrap items-start gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Sparkles className="h-5 w-5 text-amber-500" />
            <div>
              <h1 className="font-heading text-xl tracking-tight text-stone-900">AI Comment Analysis</h1>
              <p className="text-xs text-stone-500 mt-0.5">
                {empty
                  ? "Auto-runs daily at 6am IST. Or trigger one manually."
                  : <>
                      {fmtDateTime(data.last_run_at)} · {data.analysed_this_run ?? 0} updated · {items.length} active
                      {data.last_run_by ? ` · ${data.last_run_by}` : ""}
                    </>}
              </p>
            </div>
          </div>
          <button onClick={refresh} disabled={refreshing} data-testid="refresh-ai-insights"
            className="inline-flex items-center gap-1.5 text-xs font-medium bg-stone-900 hover:bg-stone-800 text-white rounded-md px-3 py-2 transition-colors disabled:opacity-60">
            {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />}
            {refreshing ? "Analysing…" : "Re-run analysis"}
          </button>
        </div>
        {data?.gemini_error && (
          <div data-testid="gemini-warning"
            className="mt-3 inline-flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>Gemini analysis unavailable: {data.gemini_error}. Falling back to rule-based misses.</span>
          </div>
        )}
      </header>

      <div className="flex-1 overflow-auto px-5 sm:px-6 py-5">
        {loading ? (
          <div className="text-stone-400 text-sm">Loading…</div>
        ) : empty ? (
          <div data-testid="ai-empty" className="text-center py-16">
            <Sparkles className="h-8 w-8 text-amber-400 mx-auto mb-3" />
            <p className="text-stone-700 font-medium">No analysis yet</p>
            <p className="text-stone-500 text-sm mt-1 max-w-md mx-auto">
              Daily auto-run hasn&apos;t fired yet. Click <em>Re-run analysis</em> to do it now.
            </p>
          </div>
        ) : items.length === 0 && patterns.length === 0 && recs.length === 0 ? (
          <div data-testid="ai-allclear" className="text-center py-16">
            <Check className="h-8 w-8 text-emerald-500 mx-auto mb-3" />
            <p className="text-stone-700 font-medium">All clear</p>
            <p className="text-stone-500 text-sm mt-1">No new actions to surface. Items reappear when comments are updated.</p>
          </div>
        ) : (
          <div className="grid gap-5 lg:grid-cols-2">
            <section data-testid="missed-section">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-4 w-4 text-red-600" />
                <h2 className="text-sm font-semibold tracking-tight text-stone-900">Missed actions</h2>
                <span className="text-xs text-stone-400">{missed.length}</span>
              </div>
              {missed.length === 0
                ? <p className="text-sm text-stone-400 py-4">Nothing overdue.</p>
                : <div className="space-y-2">{missed.map((it) => <InsightCard key={it.id} it={it} onOpen={setActiveId} onDismiss={dismiss} />)}</div>}
            </section>

            <section data-testid="opportunity-section">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-4 w-4 text-emerald-600" />
                <h2 className="text-sm font-semibold tracking-tight text-stone-900">Opportunities</h2>
                <span className="text-xs text-stone-400">{opps.length}</span>
              </div>
              {opps.length === 0
                ? <p className="text-sm text-stone-400 py-4">No fresh signals.</p>
                : <div className="space-y-2">{opps.map((it) => <InsightCard key={it.id} it={it} onOpen={setActiveId} onDismiss={dismiss} />)}</div>}
            </section>

            {(patterns.length > 0 || recs.length > 0) && (
              <section data-testid="patterns-section" className="lg:col-span-2">
                <div className="bg-white border border-stone-200 rounded-xl p-4 grid gap-5 md:grid-cols-2">
                  {patterns.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="h-4 w-4 text-violet-600" />
                        <h2 className="text-sm font-semibold text-stone-900">Patterns</h2>
                      </div>
                      <ul className="space-y-2.5">
                        {patterns.map((p, i) => (
                          <li key={i} data-testid={`pattern-${i}`} className="text-xs text-stone-700 leading-snug">
                            <span className="font-medium text-stone-900">{p.title}</span>
                            {p.summary && <> — <span className="text-stone-600">{p.summary}</span></>}
                            {Array.isArray(p.leads) && p.leads.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1.5">
                                {p.leads.map((ld) => (
                                  <button key={ld.id} onClick={() => setActiveId(ld.id)}
                                    data-testid={`pattern-${i}-lead-${ld.id}`}
                                    className="text-[10px] bg-violet-50 hover:bg-violet-100 text-violet-800 border border-violet-200 rounded-full px-2 py-0.5 transition-colors truncate max-w-[180px]">
                                    {ld.name}
                                  </button>
                                ))}
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {recs.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Lightbulb className="h-4 w-4 text-amber-600" />
                        <h2 className="text-sm font-semibold text-stone-900">Recommendations</h2>
                      </div>
                      <ul className="space-y-2.5">
                        {recs.map((r, i) => {
                          const text = typeof r === "string" ? r : (r?.text || "");
                          const leadsList = (typeof r === "object" && Array.isArray(r?.leads)) ? r.leads : [];
                          return (
                            <li key={i} data-testid={`rec-${i}`} className="text-xs text-stone-700 leading-snug">
                              <span>• {text}</span>
                              {leadsList.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1.5 ml-3">
                                  {leadsList.map((ld) => (
                                    <button key={ld.id} onClick={() => setActiveId(ld.id)}
                                      data-testid={`rec-${i}-lead-${ld.id}`}
                                      className="text-[10px] bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5 transition-colors truncate max-w-[180px]">
                                      {ld.name}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              </section>
            )}
          </div>
        )}
      </div>

      {activeId && (
        <LeadDrawer leadId={activeId} onClose={() => setActiveId(null)} onSaved={load} />
      )}
    </div>
  );
}
